import { useEffect, useState, useRef } from "react";
import Navbar from "../components/header/Navbar";
import SidebarDrawer from "../components/SidebarDrawer";
import LeafletMap from "../components/LeafletMap";
import { getPredictedPhotos, getPhotoLikeStatus } from "../services/photoService";
import type { Photo } from "../types/photo";

export type SelectedPhoto = Photo & {
  uploaderName: string;
  liked: boolean;
  likeCount: number;
};

const MapPage = () => {
  const [photos, setPhotos] = useState<SelectedPhoto[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<SelectedPhoto | null>(null);
  const [mobileDrawerState, setMobileDrawerState] = useState<"closed" | "preview" | "expanded">("closed");
  const [isMobileView, setIsMobileView] = useState<boolean>(window.innerWidth < 768);

  const startYRef = useRef<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const predictedPhotos = await getPredictedPhotos();
        const mappedPhotos = predictedPhotos.map(photo => ({
          ...photo,
          uploaderName: photo.uploadedBy.name,
          liked: false,
          likeCount: 0,
        }));
        setPhotos(mappedPhotos);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchData();
  }, []);

  const handleMarkerClick = async (photo: Photo | null) => {
    if (photo) {
      const uploaderName = photo.uploadedBy.name;
      try {
        const likeStatus = await getPhotoLikeStatus(photo.photoId);
        const updatedPhoto = { ...photo, uploaderName, liked: likeStatus.liked, likeCount: likeStatus.likeCount };
        setPhotos(prev => prev.map(p => (p.photoId === photo.photoId ? updatedPhoto : p)));
        setSelectedPhoto(updatedPhoto);
        setTimeout(() => setMobileDrawerState("expanded"), 300);
      } catch (error) {
        console.error("Failed to load like status:", error);
      }
    } else {
      setSelectedPhoto(null);
      setMobileDrawerState("closed");
    }
  };

  const handleLikeToggle = (newLiked: boolean, newLikeCount: number) => {
    if (!selectedPhoto) return;
    const updatedPhoto = { ...selectedPhoto, liked: newLiked, likeCount: newLikeCount };
    setSelectedPhoto(updatedPhoto);
    setPhotos(prev => prev.map(p => (p.photoId === updatedPhoto.photoId ? updatedPhoto : p)));
  };

  const toggleDrawer = () => {
    setMobileDrawerState(prev => (prev === "expanded" ? "preview" : "expanded"));
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    startYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (startYRef.current !== null) {
      const endY = e.changedTouches[0].clientY;
      const diff = endY - startYRef.current;
      if (diff > 50) {
        setMobileDrawerState("preview");
      } else if (diff < -50) {
        setMobileDrawerState("expanded");
      }
      startYRef.current = null;
    }
  };

  return (
    <>
      {/* Top navigation bar */}
      <Navbar />

      {/* Main container */}
      <div className="flex flex-col md:flex-row flex-1 h-[calc(100vh-64px)]">
        
        {/* Map display area */}
        <div
          className={`h-full ${selectedPhoto ? "flex-grow" : "w-full"}`}
          onClick={() => {
            setSelectedPhoto(null);
            setMobileDrawerState("closed");
          }}
        >
          <LeafletMap
            mode="display"
            enableSearch
            lat={-34.9285}
            lng={138.6007}
            markersFromBackend={photos}
            onMarkerClick={handleMarkerClick}
            zoom={5}
          />
        </div>

        {/* Desktop Sidebar */}
        {selectedPhoto && !isMobileView && (
          <div className="w-[400px] h-full overflow-y-auto border-l bg-background z-10">
            <SidebarDrawer selectedMarker={selectedPhoto} onLikeToggle={handleLikeToggle} />
          </div>
        )}

        {/* Mobile drawer (Preview or Expanded) */}
        {selectedPhoto && isMobileView && mobileDrawerState !== "closed" && (
          <div
            className={`fixed bottom-0 left-0 right-0 bg-background border-t z-50 transition-all duration-300 ${
              mobileDrawerState === "expanded" ? "h-[70vh]" : "h-[10vh]"
            }`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Drag handle section */}
            <div className="relative bg-background h-8 flex justify-center items-center mb-0">
              <div
                className="w-10 h-1.5 bg-primary rounded-full opacity-70"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDrawer();
                }}
              />
            </div>

            {/* Drawer content area */}
            <div className="h-[calc(100%-2rem)] overflow-y-auto">
              <SidebarDrawer
                selectedMarker={selectedPhoto}
                onLikeToggle={handleLikeToggle}
                onToggleDrawer={toggleDrawer}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MapPage;